import React from 'react';
import PropTypes from 'prop-types';
import { Alert, Badge, Dropdown, MenuItem, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Card } from 'react-bootstrap-5';
import { listItemPropTypes, reorderableListItem } from 'helpers/react_dnd/reorderable_list';
import UnstyledDropdownToggle from '../bootstrap_extensions/unstyled_dropdown_toggle';
import downdloadPhoto from './download_photo';
import { isEmpty } from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCog, faExclamationCircle, faTrashAlt } from '@fortawesome/pro-solid-svg-icons';

import Button from 'shared/button';
import { faCheck, faEllipsisVertical, faExclamation, faPenToSquare } from '@fortawesome/pro-solid-svg-icons';

const BUILDOUT_MEDIA_PHOTO = 'Buildout Media Photo';

export class PhotoRow extends React.Component {
  static propTypes = {
    ...listItemPropTypes,
    classSlug: PropTypes.string.isRequired,
    defaultPhotoOption: PropTypes.string.isRequired,
    isChecked: PropTypes.bool,
    mediaUiUpdate: PropTypes.bool,
    photoAttachment: PropTypes.object.isRequired,
    photoEditorUrl: PropTypes.string,
    photoTypeOptions: PropTypes.array.isRequired,
    showEdit: PropTypes.bool,
    userAdmin: PropTypes.bool,
    onAttributeChange: PropTypes.func.isRequired,
    onCheckboxClicked: PropTypes.func,
    onEdit: PropTypes.func.isRequired
  }

  static defaultPropTypes = {
    isChecked: false,
    mediaUiUpdate: false,
    showEdit: true
  }

  handleDescriptionUpdate = (e) => {
    this.props.onAttributeChange(this.props.index, 'description', e.target.value);
  }

  handleAttachmentTypeUpdate = (e) => {
    this.props.onAttributeChange(this.props.index, 'attachmentType', e.target.value);
  }

  handleSyndicateUpdate = (newValue) => {
    this.props.onAttributeChange(this.props.index, 'syndicate', newValue);
  }

  deletePhoto = (e) => {
    e.preventDefault();
    this.props.onAttributeChange(this.props.index, 'deleted', true);
  }

  sendToTop = (e) => {
    e.preventDefault();
    this.props.onReorder(this.props.index, 0);
  }

  getInputName = (key) => {
    let name = `${this.props.classSlug}[photo_attachments_attributes][${this.props.index}]`;
    const photoAttributeKeys = [
      'upload_id',
      '_destroy',
      'attachment_type',
      'id'
    ];

    if (photoAttributeKeys.indexOf(key) > -1) {
      name += `[photo_attributes]`;
    }
    name += `[${key}]`;
    return name;
  }

  render() {
    if (this.props.photoAttachment.deleted && this.props.photoAttachment.uploaded) {
      return null;
    }

    const { isChecked, photoAttachment, index } = this.props;
    const style = {
      opacity: this.props.isDragging ? 0 : 1,
      display: photoAttachment.deleted ? 'none' : 'block'
    };
    const failedProcessing = this.props.photoAttachment.sidekiqStatus === 'failed';

    const photoRowBody = this.props.connectDragPreview(this.props.connectDropTarget(
      <div className="photo-row" key={index} name="photo-row-container" style={style}>
        <Card>
          <Card.Header className="position-relative">
            {this.renderDragSource()}
            <div className="menu-container d-flex">
              <input checked={isChecked} type="checkbox" onChange={this.props.onCheckboxClicked}/>
            </div>
            <div className="position-absolute d-flex align-items-center gap-3" style={{ right: '10px', top: '5.5px' }}>
              {this.renderStatusBadge()}
              {this.renderSyndicationInput()}
            </div>
          </Card.Header>
          <Card.Body>
            {this.renderDescriptionInput(photoAttachment, index)}
            <div
              className="d-flex align-items-center gap-3"
              style={{ padding: this.props.showEdit ? '0 4px 0 12px' : '0px 4px' }}
            >
              {this.props.showEdit && this.renderEditButton()}
              <div className={`photo-attachment-type w-100 ${this.props.showEdit ? 'text-right' : ''}`} >
                { this.renderAttachmentTypeInput() }
              </div>
            </div>
            <div className="hidden-fields">
              {this.renderHiddenIdField()}
              {this.renderHiddenSortOrderField()}
              {this.renderHiddenDeleteField()}
            </div>
          </Card.Body>
        </Card>
      </div>
    ));

    const oldPhotoRowBody = this.props.connectDragPreview(this.props.connectDropTarget(
      <div className="photo-row shadowed bottom-buffer-lg" key={index} name="photo-row-container" style={style} >
        <div className="row">
          <div className="col-xs-3">
            {this.renderDragSource()}
          </div>

          {this.renderHiddenIdField()}
          {this.renderHiddenSortOrderField()}
          {this.renderHiddenDeleteField()}
          {failedProcessing ?
            <div className="col-xs-9 pl-0">
              <Alert bsStyle="danger" className="my-2 mr-4">
                <div className="d-flex">
                  <FontAwesomeIcon className="ml-3" icon={faExclamationCircle} />
                  <span className="ml-3 flex-grow-1">
                    <p className="mb-0"><b>Photo has failed to upload</b></p>
                    <p>Reduce the image size and try again</p>
                  </span>
                  <FontAwesomeIcon className="mr-3 clickable" icon={faTrashAlt} onClick={this.deletePhoto} />
                </div>
              </Alert>
            </div> :
            <div className="col-xs-9">
              <div className="photo-inputs-container">
                <div className="photo-inputs">
                  <div className="row">
                    <div className="col-xs-12 col-lg-8">
                      {this.renderDescriptionInput(photoAttachment, index)}
                    </div>
                    <div className="col-xs-12 col-lg-4 d-flex">
                      {this.renderAttachmentTypeInput()}
                      <span>{this.renderPhotoOptions(photoAttachment, index)}</span>
                    </div>
                  </div>
                </div>
                {this.renderSyndicationInput(photoAttachment, index)}
              </div>
            </div>
          }
        </div>
      </div>
    ));

    return (
      <React.Fragment>
        { this.props.mediaUiUpdate ? photoRowBody : oldPhotoRowBody }
      </React.Fragment>
    );
  }

  newSidekiqImage() {
    return !isEmpty(this.props.photoAttachment.sidekiqStatus);
  }

  bannerLabel() {
    if (this.newSidekiqImage()) {
      return this.props.photoAttachment.sidekiqStatus;
    } else {
      return 'Processing';
    }
  }

  photoPreviewStyle() {
    const { mediaUiUpdate } = this.props;
    const { processing, sidekiqStatus: status } = this.props.photoAttachment;
    const url = mediaUiUpdate ? this.props.photoAttachment.url : this.props.photoAttachment.thumbnailUrl;

    if (processing && (status === 'complete' || status === undefined)) {
      return { backgroundImage: `url(${url})` };
    } else if (processing) {
      return { backgroundImage: 'none' };
    } else {
      return { backgroundImage: `url(${url})` };
    }
  }

  renderTooltip() {
    const status = this.props.photoAttachment.sidekiqStatus;

    if (status === 'failed') {
      return (
        <Tooltip id={`primaryPhotoTooltip${this.props.index}`}>
          This media failed to process. This was mostly likely caused by an image that is too large.
          Try reducing the image size or contact Buildout support.
        </Tooltip>
      );
    } else if (status !== 'complete' && status != null) {
      return (
        <Tooltip id={`primaryPhotoTooltip${this.props.index}`}>
          Your media is being optimized for the best performance. This may take a few minutes.
        </Tooltip>
      );
    } else {
      return (
        <Tooltip id={`primaryPhotoTooltip${this.props.index}`}>
          Primary photos will be the first to be displayed in documents, email blasts and plugins.
          Choose a photo that best represents this {this.props.classSlug} as the primary photo.
        </Tooltip>
      );
    }
  }

  processingPhotoLabelClass() {
    const status = this.props.photoAttachment.sidekiqStatus;

    if (status === 'failed') {
      return 'photo-label-failed';
    } else if (status === 'complete') {
      return 'photo-label-complete';
    } else {
      return 'photo-label';
    }
  }

  renderDragSource() {
    const labelStyle = {
      display: this.props.index === 0 ? 'block' : 'none'
    };

    if (this.props.mediaUiUpdate) {
      return this.props.connectDragSource(
        <div className="js-photo-preview" style={this.photoPreviewStyle()} />
      );
    } else {
      return this.props.connectDragSource(
        <div className="js-photo-preview" style={this.photoPreviewStyle()}>
          <OverlayTrigger
            overlay={this.renderTooltip()}
            placement="left"
          >
            {this.props.photoAttachment.processing ?
              <h6
                className={`text-center ${this.processingPhotoLabelClass()}`}
                name="processingLabel"
              >
                {this.bannerLabel()}
              </h6> :
              <h6
                className="text-center photo-label"
                name="photoLabel"
                style={labelStyle}
              >
                Primary
              </h6>
            }
          </OverlayTrigger>
        </div>
      );
    }
  }

  renderStatusBadge() {
    const labelStyle = {
      display: this.props.index === 0 ? 'block' : 'none'
    };

    return (
      <div className="photo-badge-container">
        <OverlayTrigger
          overlay={this.renderTooltip()}
          placement="top"
        >
          {
            this.props.photoAttachment.processing ?
              <Badge className="photo-label-badge">{this.bannerLabel()}</Badge> :
              <Badge className="photo-label-badge" style={labelStyle}>Primary</Badge>
          }
        </OverlayTrigger>
      </div>
    );
  }

  renderHiddenIdField() {
    const { photoAttachment, index } = this.props;
    if (photoAttachment.uploaded) {
      return (
        <input
          name={this.getInputName('upload_id')}
          readOnly
          type="hidden"
          value={photoAttachment.uploadId}
        />
      );
    }
    let newUploadIdField = null;
    if (photoAttachment.uploadId) {
      newUploadIdField = (
        <input
          name={this.getInputName('upload_id')}
          readOnly
          type="hidden"
          value={photoAttachment.uploadId}
        />
      );
    }
    return (
      <React.Fragment>
        {newUploadIdField}
        <input
          name={`${this.props.classSlug}[photo_attachments_attributes][${index}][id]`}
          readOnly
          type="hidden"
          value={photoAttachment.id}
        />
        <input
          name={this.getInputName('photo_id')}
          readOnly
          type="hidden"
          value={photoAttachment.photoId}
        />
        <input
          name={this.getInputName('id')}
          readOnly
          type="hidden"
          value={photoAttachment.photoId}
        />
      </React.Fragment>
    );
  }

  renderHiddenSortOrderField() {
    return (
      <input
        name={this.getInputName('sort_order')}
        readOnly
        type="hidden"
        value={this.props.index}
      />
    );
  }

  renderHiddenDeleteField() {
    return (
      <input
        className="deletePhotoInput"
        name={this.getInputName('_destroy')}
        readOnly
        type="hidden"
        value={this.props.photoAttachment.deleted ? '1' : '0'}
      />
    );
  }

  renderDescriptionInput() {
    // TODO: Investigate if we can remove the "property_photo_attachments_description" from this class
    return this.props.mediaUiUpdate ? (
      <input
        className="form-control string optional"
        name={this.getInputName('description')}
        placeholder="Add Caption"
        type="text"
        value={this.props.photoAttachment.description || ''}
        onChange={this.handleDescriptionUpdate}
      />
    ) : (
      <div className="form-group string optional property_photo_attachments_description inline top-buffer">
        <div className="form-input-container">
          <input
            className="form-control string optional"
            name={this.getInputName('description')}
            placeholder="Description"
            type="text"
            value={this.props.photoAttachment.description || ''}
            onChange={this.handleDescriptionUpdate}
          />
        </div>
      </div>
    );
  }

  disablePhotoTypeOptions() {
    const { userAdmin, photoAttachment } = this.props;
    return (!userAdmin && photoAttachment.attachmentType === BUILDOUT_MEDIA_PHOTO);
  }

  displayedPhotoTypeOptions() {
    const { photoTypeOptions } = this.props;
    if (this.disablePhotoTypeOptions()) {
      return <option key={BUILDOUT_MEDIA_PHOTO} value={BUILDOUT_MEDIA_PHOTO}>{BUILDOUT_MEDIA_PHOTO}</option>;
    } else {
      return photoTypeOptions.map(option => <option key={option} value={option}>{option}</option>);
    }
  }

  handlePhotoDownload() {
    downdloadPhoto(this.props.photoAttachment.url, window, document).catch((e) => {
      bootbox.alert('There was an error downloading the photo. Please try again later.');

      // eslint-disable-next-line no-console
      console.error(`There was an error downloading the photo
        with url:[${this.props.photoAttachment.url}]`, e);
    });
  }

  renderAttachmentTypeInput() {
    const { defaultPhotoOption, mediaUiUpdate, photoAttachment, photoTypeOptions } = this.props;

    const newStyle = {
      opacity: photoTypeOptions.length > 1 ? 1 : 0,
      padding: '8px 20px 8px 8px',
      textAlign: 'inherit'
    };

    return mediaUiUpdate ? (
      <select
        className="w-100 select-right-arrow"
        disabled={this.disablePhotoTypeOptions()}
        name={this.getInputName('attachment_type')}
        style={newStyle}
        value={photoAttachment.attachmentType || defaultPhotoOption}
        onChange={this.handleAttachmentTypeUpdate}
      >
        {this.displayedPhotoTypeOptions()}
      </select>
    ) : (
      <div
        className="form-group select optional property_photo_attachments_photo_attachment_type
                    inline flex-grow-1 top-buffer"
      >
        <div className="form-input-container">
          <select
            className="form-control select optional select-right-arrow photo-attachment-select"
            disabled={this.disablePhotoTypeOptions()}
            name={this.getInputName('attachment_type')}
            style={{ opacity: photoTypeOptions.length > 1 ? 1 : 0 }}
            value={photoAttachment.attachmentType || defaultPhotoOption}
            onChange={this.handleAttachmentTypeUpdate}
          >
            {this.displayedPhotoTypeOptions()}
          </select>
        </div>
      </div>
    );
  }

  renderSyndicationInput() {
    const { processing, syndicate } = this.props.photoAttachment;
    const { mediaUiUpdate } = this.props;

    const classString = mediaUiUpdate ? ''
      : 'form-group boolean optional property_photo_attachments_syndicate  top-buffer-sm';

    let tooltipText = 'By checking this box, you agree that this media is eligible for syndication.';
    if (mediaUiUpdate) tooltipText = `This photo is${syndicate ? '' : ' not'} approved for Syndication`;

    const tooltip = <Tooltip id={`propertySyndicationTooltip${this.props.index}`}>{tooltipText}</Tooltip>;

    return (
      <OverlayTrigger overlay={tooltip} placement="top">
        {mediaUiUpdate ? (
          <button
            className="photo-syndication-button"
            onClick={() => this.handleSyndicateUpdate(!this.props.photoAttachment.syndicate)}
          >
            {this.props.photoAttachment.syndicate ? (
              <FontAwesomeIcon className="ds-text-secondary" icon={faCheck} size="lg"/>
            ) : (
              <FontAwesomeIcon className="ds-text-danger" icon={faExclamation} size="lg"/>
            )}
          </button>
        ) : (
          <div className={`photo-syndication d-block ${mediaUiUpdate ? '' : 'px-4'}`}>
            <div className={classString}>
              <div className={mediaUiUpdate ? '' : 'checkbox'}>
                <input
                  name={this.getInputName('syndicate')}
                  type="hidden"
                  value={0}
                />
                <label className={mediaUiUpdate ? '' : 'boolean optional'}>
                  <input
                    checked={this.props.photoAttachment.syndicate}
                    className={mediaUiUpdate ? 'm-0' : ''}
                    disabled={processing}
                    name={this.getInputName('syndicate')}
                    type="checkbox"
                    onChange={e => this.handleSyndicateUpdate(e.target.checked)}
                  />
                  {!mediaUiUpdate && 'Approve'}
                </label>
              </div>
              {!mediaUiUpdate &&
                <p className="help-block">
                  I am licensed to use this photo to market my listing
                </p>
              }
            </div>
          </div>
        )}
      </OverlayTrigger>
    );
  }

  renderEditButton() {
    const hideEdit = this.props.photoEditorUrl &&
      this.props.photoAttachment.attachmentType === BUILDOUT_MEDIA_PHOTO;

    return (
      <Button.Secondary
        disabled={hideEdit}
        icon={faPenToSquare}
        plain
        style={{ padding: '0 4px', margin: '0 -4px' }}
        onClick={() => !hideEdit && this.props.onEdit(this.props.photoAttachment)}
      >
        Edit
      </Button.Secondary>
    );
  }

  renderPhotoOptions() {
    const { index, mediaUiUpdate, photoAttachment, photoEditorUrl } = this.props;
    const showEdit = photoEditorUrl && photoAttachment.attachmentType !== BUILDOUT_MEDIA_PHOTO;
    const accessible = !photoAttachment.processing || photoAttachment.sidekiqStatus === 'complete';

    if (mediaUiUpdate) {
      return (
        <Dropdown className="d-flex" id={`photoOptions${index}`}>
          <UnstyledDropdownToggle bsRole="toggle" className="dropdown-toggle">
            <FontAwesomeIcon className="clickable" icon={faEllipsisVertical} size="lg" />
          </UnstyledDropdownToggle>
          <Dropdown.Menu className="photoOptions">
            {accessible &&
              <React.Fragment>
                <MenuItem
                  className="previewLink"
                  eventKey="1"
                  href={photoAttachment.url}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  Preview
                </MenuItem>
                <MenuItem
                  eventKey="5"
                  onClick={() => this.handlePhotoDownload()}
                >
                  Download
                </MenuItem>

                <MenuItem
                  className={index == 0 ? 'hidden' : ''}
                  eventKey="2"
                  name="sendPhotoToTop"
                  onClick={this.sendToTop}
                >
                  Use as main photo
                </MenuItem>
              </React.Fragment>
            }
            <MenuItem
              eventKey="3"
              name="deletePhotoButton"
              onClick={this.deletePhoto}
            >
              Delete
            </MenuItem>
          </Dropdown.Menu>
        </Dropdown>
      );
    } else {
      return (
        <Dropdown className="photo-options" id={`photoOptions${index}`}>
          <UnstyledDropdownToggle bsRole="toggle">
            <FontAwesomeIcon className="text-muted" icon={faCog} size="lg"/>
          </UnstyledDropdownToggle>
          <Dropdown.Menu className="photoOptions">
            {accessible &&
              <React.Fragment>
                <MenuItem
                  className="previewLink"
                  eventKey="1"
                  href={photoAttachment.url}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  Preview
                </MenuItem>
                <MenuItem
                  eventKey="5"
                  onClick={() => this.handlePhotoDownload()}
                >
                  Download
                </MenuItem>

                {showEdit && this.renderEditOption()}

                <MenuItem
                  className={index == 0 ? 'hidden' : ''}
                  eventKey="2"
                  name="sendPhotoToTop"
                  onClick={this.sendToTop}
                >
                  Use as main photo
                </MenuItem>
              </React.Fragment>
            }
            <MenuItem
              eventKey="3"
              name="deletePhotoButton"
              onClick={this.deletePhoto}
            >
              Delete
            </MenuItem>
          </Dropdown.Menu>
        </Dropdown>
      );
    }
  }

  renderEditOption() {
    return (
      <MenuItem
        eventKey="4"
        name="edit-photo"
        onClick={() => this.props.onEdit(this.props.photoAttachment)}
      >
        Edit
      </MenuItem>
    );
  }
}

export default reorderableListItem(PhotoRow, 'photoForm', {
  beginDrag(props) {
    return {
      id: props.photoAttachment.id,
      index: props.index
    };
  }
});
